import { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { apiError } from "@/lib/api";
import { THEMES, logoSrc } from "@/lib/themes";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { BusinessSettings } from "@/components/BusinessSettings";
import { ReminderSettings } from "@/components/ReminderSettings";
import { ReportSettings } from "@/components/ReportSettings";
import { cn } from "@/lib/utils";

export default function Settings() {
  const { theme, saveTheme } = useApp();
  const [busy, setBusy] = useState("");

  const apply = async (id) => {
    setBusy(id);
    try {
      await saveTheme(id);
      toast.success(`${THEMES.find((t) => t.id === id).name} applied for all users`);
    } catch (e) {
      toast.error(apiError(e).message);
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader eyebrow="Configuration" title="Settings" subtitle="Business details, statement schedule and the app-wide theme." />
      <BusinessSettings />
      <ReminderSettings />
      <ReportSettings />
      <h3 className="mb-4 text-lg font-semibold">Theme</h3>
      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3" data-testid="theme-picker">
        {THEMES.map((t, i) => {
          const active = theme === t.id;
          return (
            <div key={t.id} data-testid={`theme-card-${t.id}`} style={{ animationDelay: `${i * 60}ms` }}
              className={cn("card-lift fade-up overflow-hidden rounded-lg border bg-card", active && "ring-2 ring-primary")}>
              <div className="flex h-44 items-center justify-center border-b p-6" style={{ backgroundColor: t.surface }}>
                <img src={logoSrc(t.id)} alt={`${t.name} logo`} className="h-full w-auto max-w-full object-contain" draggable={false} />
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-display text-lg font-semibold">{t.name}</div>
                    {t.note && <div className="text-xs text-muted-foreground">{t.note}</div>}
                  </div>
                  <div className="flex -space-x-1.5">
                    {t.swatches.map((c) => <span key={c} className="h-5 w-5 rounded-full border-2 border-card shadow-sm" style={{ backgroundColor: c }} />)}
                  </div>
                </div>
                <Button className="mt-5 w-full" variant={active ? "secondary" : "outline"} disabled={active || !!busy}
                  onClick={() => apply(t.id)} data-testid={`apply-theme-${t.id}`}>
                  {busy === t.id ? <Loader2 className="animate-spin" /> : active ? <Check /> : null}
                  {active ? "Active theme" : "Apply app-wide"}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
